import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Download, ScrollText } from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/enterprise.css';
import '../styles/responsive/pages/AdminLogs.css';
import { useTheme } from '../context/ThemeContext';
import useDebounce from '../hooks/useDebounce';
import PageHeaderPro from '../components/common/PageHeaderPro';
import FilterTabs from '../components/common/FilterTabs';
import EntSearch from '../components/common/EntSearch';
import Pager from '../components/common/Pager';
import StatusBadge from '../components/common/StatusBadge';
import { TableSkeleton } from '../components/common/Skeleton';
import { useGetLogsQuery, buildLogExportUrl } from '../store/api/adminOpsApi';

const SURFACES = [
  { key: 'ip', label: 'IP' },
  { key: 'merchant', label: 'Merchant' },
  { key: 'customer', label: 'Customer' },
  { key: 'admin', label: 'Admin Audit' },
  { key: 'security', label: 'Security' },
];

const SEVERITIES = ['', 'info', 'warn', 'critical'];

const PAGE_SIZE = 20;

// Map a log severity to the StatusBadge vocabulary.
const sevForBadge = (s) => {
  if (s === 'critical') return 'failed';
  if (s === 'warn' || s === 'warning') return 'warning';
  return 'info';
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminLogs = () => {
  const { mode } = useTheme();
  const { token } = useSelector((state) => state.auth);

  const [surface, setSurface] = useState('admin');
  const [search, setSearch] = useState('');
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [severity, setSeverity] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  const filters = {
    q: debouncedSearch,
    actor,
    action,
    severity,
    from,
    to,
    page,
    limit: PAGE_SIZE,
  };

  const { data, isLoading, isFetching } = useGetLogsQuery(
    { surface, ...filters },
    { refetchOnMountOrArgChange: true }
  );

  const rows = data?.logs || data?.rows || [];
  const total = data?.pagination?.total ?? rows.length;
  const totalPages = data?.pagination?.totalPages ?? 1;

  const resetPage = () => setPage(1);

  const handleSurface = (key) => {
    setSurface(key);
    resetPage();
  };

  // Authed blob download — the export endpoint is watermarked and logs its own
  // export audit event server-side (spec §6 / §8).
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const url = buildLogExportUrl(surface, { q: debouncedSearch, actor, action, severity, from, to }, format);
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `logs-${surface}-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      toast.success(`Exported ${surface} logs (${format.toUpperCase()})`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const tabs = SURFACES.map((s) => ({ key: s.key, label: s.label }));

  return (
    <div className="ent ent-page admin-logs-page">
      <PageHeaderPro
        title="Logs"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={isFetching ? 'Updating…' : 'Updated just now'}
        actions={
          <>
            <button
              className="ent-btn ent-btn-secondary"
              onClick={() => handleExport('csv')}
              disabled={exporting}
            >
              <Download size={16} /> CSV
            </button>
            <button
              className="ent-btn ent-btn-secondary"
              onClick={() => handleExport('json')}
              disabled={exporting}
            >
              <Download size={16} /> JSON
            </button>
          </>
        }
      />

      <FilterTabs
        tabs={tabs}
        active={surface}
        onChange={handleSurface}
        right={
          <EntSearch
            value={search}
            onChange={(v) => {
              setSearch(v);
              resetPage();
            }}
            placeholder="Search logs…"
          />
        }
      />

      <div className="logs-filters">
        <label className="logs-filter">
          <span>Actor</span>
          <input
            type="text"
            value={actor}
            onChange={(e) => {
              setActor(e.target.value);
              resetPage();
            }}
            placeholder="Actor id / email"
          />
        </label>
        <label className="logs-filter">
          <span>Action</span>
          <input
            type="text"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              resetPage();
            }}
            placeholder="e.g. payout.approve"
          />
        </label>
        <label className="logs-filter">
          <span>Severity</span>
          <select
            value={severity}
            onChange={(e) => {
              setSeverity(e.target.value);
              resetPage();
            }}
          >
            {SEVERITIES.map((s) => (
              <option key={s || 'all'} value={s}>
                {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
              </option>
            ))}
          </select>
        </label>
        <label className="logs-filter">
          <span>From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              resetPage();
            }}
          />
        </label>
        <label className="logs-filter">
          <span>To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              resetPage();
            }}
          />
        </label>
      </div>

      <div className="pro-table-wrap">
        {isLoading ? (
          <TableSkeleton rows={PAGE_SIZE} cols={5} />
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <ScrollText size={48} />
            <p>No log entries</p>
            <span>Adjust the filters or pick another surface</span>
          </div>
        ) : (
          <table className="pro-table">
            <thead>
              <tr>
                <th>TIME</th>
                <th>ACTOR</th>
                <th>ACTION</th>
                <th>DETAIL</th>
                <th>SEVERITY</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td data-label="TIME">
                    <span className="cell-2__sub mono">{fmtDateTime(r.created_at || r.createdAt)}</span>
                  </td>
                  <td data-label="ACTOR">
                    <div className="cell-2">
                      <span className="cell-2__main">{r.actor || r.actorEmail || r.ip || '—'}</span>
                      {r.ip && <span className="cell-2__sub mono">{r.ip}</span>}
                    </div>
                  </td>
                  <td data-label="ACTION">
                    <span className="mono">{r.action || '—'}</span>
                  </td>
                  <td data-label="DETAIL">
                    <span className="logs-detail">{r.detail || r.message || '—'}</span>
                  </td>
                  <td data-label="SEVERITY">
                    <StatusBadge status={sevForBadge(r.severity)} label={r.severity || 'info'} dot />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length > 0 && (
          <Pager page={page} pageCount={totalPages} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
        )}
      </div>
    </div>
  );
};

export default AdminLogs;
